import { ListClinic } from '@chiroup/core/types/Clinic.type';
import { useDebounce } from '@chiroup/hooks/useDebounce';
import { useState } from 'react';
import { QueryFunctionContext, useInfiniteQuery } from 'react-query';
import clinicService from '../services/clinic.service';

const getQuery = () => {
  return async (context: QueryFunctionContext) => {
    const searchTerm = context.queryKey[1] as string;
    const search = searchTerm || '';

    return clinicService.list({ skip: context.pageParam, search });
  };
};

const useClinics = (opts?: { skipEmptySearch?: boolean }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery);
  const {
    status,
    data,
    error,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery<{ data: ListClinic[]; skip: number }>(
    ['clinics', debouncedSearch],
    getQuery(),
    {
      getNextPageParam: lastGroup => lastGroup?.skip || undefined,
      refetchOnWindowFocus: false,
      // Opt-in laziness (e.g. a typeahead that shouldn't list every clinic on
      // mount): only query once there's a search term. Default stays eager for
      // the full clinic list (ClinicList).
      enabled: !opts?.skipEmptySearch || String(debouncedSearch ?? '').trim().length > 0,
    }
  );

  const onSearch = (params: string) => {
    setSearchQuery(params);
  };

  return {
    status,
    data,
    error,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
    onSearch,
    searchQuery,
  };
};

export default useClinics;
